import React from 'react';
import { getSafeRandom, clearOneTypeToast, toast, getLangCode } from '@platform/api';
import EditTable from "./Table";
import { isValidType } from '@platform/template';

import { ROW_STATUS } from '../../utils';
import { createUpdateAfterBatchChange } from '../utils';
import GridTable from "../GridTable/Table";

const generateKey = () => String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12);
const loginLanguage = getLangCode();

/**
 * 创建EditTable
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Config} config TODO:
 * @returns {EditTableComponent}
 */
export function createEditTable(id, config = {}) {
    if (!id) { return null; }
    let store = getStore.call(this, id) || setStore.call(this, id);
    store?.setConfig({ id, ...config });
    let layoutData = this.getCompPropFromLayout(id);
    let _runContext = this.getQiankunContext();
    if (Object.keys(layoutData).length) {
        config.showIndex = !layoutData.hiddenIndex;
    }
    config = {
        ...layoutData,
        ...config,
    };
    let loadedMeta;
    if (this.state.meta) {
        loadedMeta = Object.keys(this.state.meta).length;
    }

    /** @detail 必须先调用use方法并且设置模板，否则不渲染 */
    if (!store) {
        console.warn(`请先调用this.props.use.editTable('${id}');`);
        return null;
    }

    if (!loadedMeta) {
        return null;
    }

    return <EditTable
        {...config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        _runContext={_runContext}
    />;
}

/**
 * 聚焦单元格
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function focusOnCell(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    store.focusOnCell(rowKey, colKey);
}

/**
 * 设置搜索关键字
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {string} keyWord
 */
export function setKeyWord(id, keyWord) {
    execWithShare.call(this, id, tabId => {
        getStore.call(this, tabId)?.setKeyWord(keyWord);
    });
}

/**
 * 创建小卡片表格
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createSmallCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'small-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='small'
        dataSource={this.editTableDataSource}
    />;
}

/**
 * 创建大卡片表格
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createLargeCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'large-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='large'
        dataSource={this.editTableDataSource}
    />;
}

/**
 * 根据index删除行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {boolean} isDeepDel - 是否是真删
 */
export function delRow(id, dataIndexes, isDeepDel) {
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    dataIndexes = dataIndexes.map(index => Number(index));

    let store = getStore.call(this, id),
        checkedKeys = store.getCheckedKeys(),
        dataIndexSet = new Set(dataIndexes),
        rows = store.getRows(),
        deleteRowKeys = [],
        clearFocusedRow = false,
        clearClickedRow = false;

    rows.filter(row => dataIndexSet.has(row.dataIndex)).forEach(row => {
        /** @detail 从checkedKeys中移除被删行 */
        checkedKeys.delete(row.key);

        deleteRowKeys.push(row.key);

        /** @detail 如果当前点击行被删掉，则清空当前点击行数据 */
        if (row.key === store.getClickedRow()) {
            clearClickedRow = true;
        }

        /** @detail 如果当前焦点行被删掉，则清空当前焦点行数据 */
        if (row.key === store.getLastFocusedRow()) {
            clearFocusedRow = true;
        }
    });

    execWithShare.call(this, id, shareId => {
        let tableStore = getStore.call(this, shareId);
        if (!tableStore) { return; }

        !this.isUpdatePage && tableStore.getInstance().preventUpdate(true);

        clearClickedRow && (tableStore.setClickedRow());
        clearFocusedRow && (tableStore.setLastFocusedRow());
        tableStore.deleteRow(deleteRowKeys, isDeepDel);
        tableStore.setCheckedKeys(checkedKeys);

        if (isDeepDel) {
            tableStore.setCache('base', { rows: tableStore.getRows() });
        }

        !this.isUpdatePage && tableStore.getInstance().preventUpdate(false);
        tableStore.forceUpdate();
    });
}

/**
 * 根据index删除行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex|DataIndex[]} dataIndexes
 * @param {boolean} isDeepDel - 是否是真删
 * @param {function} callback
 */
export function deleteTableRowsByIndex(id, dataIndexes, isDeepDel, callback) {
    /** @detail 内部调用了delRow */
    delRow.call(this, id, dataIndexes, isDeepDel);
    callback?.();

    let store = getStore.call(this, id),
        { selectedChange } = store.getConfig();

    /** @detail 调用了config.selectedChange */
    // 原代码最后一个参数是获取选中行的数量
    selectedChange?.({ ...this.props, ...this.output }, id, store.getCheckedKeys().size);

}

/**
 * 根据index删除行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey|RowKey[]} rowKeys
 * @param {boolean} isDeepDel - 是否是真删
 */
export function deleteTableRowsByRowId(id, rowKeys, isDeepDel) {
    let store = getStore.call(this, id),
        deletedIndex = [];
    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

    rowKeys.forEach(rowKey => {
        let dataIndex = store.getRowData(rowKey).dataIndex;
        deletedIndex.push(dataIndex);
    });

    /** @detail 内部调用了delRow */
    delRow.call(this, id, deletedIndex, isDeepDel);

    let { selectedChange } = store.getConfig();
    /** @detail 调用了config.selectedChange */
    selectedChange?.({ ...this.props, ...this.output }, id, store.getCheckedKeys().size);// TODO 看下原参数getSelectedRows

}

/**
 * 获取表格所有行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} isDeepCopy - 是否进行深拷贝
 * @returns {TableRow[]}
 */
export function getAllRows(id, isDeepCopy = true) {
    let store = getStore.call(this, id);

    if (!store) { return []; }
    let checkedKeys = store.getCheckedKeys(),
        rows = store?.getRows().map(row => {
            /** @detail 行数据里的标准属性只有rowid/status/values，其他属性可能会随着版本更新而改变 */
            return {
                rowid: row.rowid,
                rowId: row.rowid, // 预算用了大写的rowId - zhuhyu
                status: row.status,
                isOptimized: row.isOptimized,
                selected: checkedKeys.has(row.key || row.rowId),
                values: row.values,
                index: row.dataIndex,
            };
        });

    isDeepCopy && (rows = JSON.parse(JSON.stringify(rows)));
    return rows;
}

/**
 * 获取所有可见的行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} isDeepCopy - 是否进行深拷贝
 * @param {boolean} isFilter - true时获取当前视图数据，false时获取非删除的数据, 默认false
 * @returns {TableRow[]}
 */
export function getVisibleRows(id, isDeepCopy = true, isFilter = false) {
    /** @detail 获取rows时内部调用了getViewData */
    let rows = getViewData.call(this, id, isFilter ? 'normal' : 'filter');
    isDeepCopy && (rows = JSON.parse(JSON.stringify(rows)));
    return rows;
}

/**
 * 获取表格行数
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} isAllRows - true时获取全部行数，false时获取可见行数
 * @returns {TableRow[]}
 */
export function getNumberOfRows(id, isAllRows) {
    return isAllRows ? getAllRows.call(this, id, false)?.length : getVisibleRows.call(this, id, false)?.length;
}

/**
 * 获取表格数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} [isDeepCopy=true] - 是否进行深拷贝
 * @returns {TableData}
 */
export function getAllData(id, isDeepCopy = true) {
    /** @detail 获取rows时内部调用了getAllRows */
    let rows = getAllRows.call(this, id, isDeepCopy),
        store = getStore.call(this, id),
        { allpks, alltss } = store.getAllRowsInfo();
    return {
        areaType: 'table',
        rows,
        areacode: null,
        allpks,
        alltss,
    };
}

/**
 * 获取视图数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {'normal' | 'filter' | 'all' | 'invisible'} [view='normal'] - normal：视图数据；filter：非删除数据；all：所有数据；invisible：不可见数据；否则返回所有数据。
 * @param {boolean} isDeepCopy - 2207未实现
 * @returns {TableRow[]}
 */
export function getViewData(id, view = 'normal', isDeepCopy = false) {
    let store = getStore.call(this, id);
    if (view === 'normal') { return store.getVisibleRows(); }
    if (view === 'filter') { return store.getRows().filter(row => row.status !== ROW_STATUS.DELETE); }
    if (view === 'all') { return store.getRows(); }
    if (view === 'invisible') {
        let rows = store.getRows();
        let visibleRowKeys = new Set(store.visibleRowKeys());
        return rows.filter(row => !visibleRowKeys.has(row.key));
    }
    return store.getRows();
}

/**
 * 获取非删除行的数据（不包含某些列）
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys
 * @returns {TableRow[]}
 */
export function getAllRowsRemoveKeys(id, keys) {
    !Array.isArray(keys) && (keys = [keys]);

    let store = getStore.call(this, id),
        rows = store.getRows();

    return rows.filter(row => row.status !== ROW_STATUS.DELETE).map(row => {
        keys.forEach(key => delete row.values[key]);
        return row;
    });
}

/**
 * 在指定位置新增行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {boolean} autoFocus - 是否自动聚焦首个单元格
 * @param {TableRow|Promise|function} defaultValue - 行默认值。如果没有指定默认值，会取模板默认值
 * @param {function} callback - 增行回调，row => {}
 * @param {boolean} isAutoAddRow - 2207未实现
 * @param {boolean} shouldForceUpdate - 2207未实现
 */
export function addRow(id, dataIndex, autoFocus = false, defaultValue = {}, callback, isAutoAddRow, shouldForceUpdate = true) {
    _addRow.call(this, id, dataIndex, defaultValue, autoFocus, row => callback?.(id, row));

    const store = getStore.call(this, id);
    const { statusChange } = store.getConfig();

    /** @detail 调用了config.statusChange */
    statusChange?.(store.getStatus());
}

/**
 * 移除空行（从数据中真删）
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys - 涉及到的列
 * @param {'except' | 'include'} rule - except：不包含的列；include：包含的列
 * @param {boolean} [isDeepDel=true] - 被过滤的数据是否是真删
 */
export function filterEmptyRows(id, keys, rule = 'except', isDeepDel = true) {
    let store = getStore.call(this, id),
        rows = store.filterEmptyRows(keys, rule, isDeepDel);

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.setRows({ rows });
    });
    updateFormData.call(this, id);
}

/**
 * 获取最后一次选中的行
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {TableRow}
 */
export function getCurrentRecord(id) {
    let store = getStore.call(this, id),
        row = store.getRowData(store.getLastCheckedKey());

    return store.getInstance()._formatOutputRow(row);
}

/**
 * 设置焦点行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {function} callback
 */
export function focusRowByIndex(id, dataIndex, callback) {
    let store = getStore.call(this, id),
        rowKey = store.getRowKeys()[dataIndex];

    /** @detail 滚动到行 */
    store.scrollIntoView({ rowKey });

    /** @detail 背景色加深 */
    store.setClickedRow(rowKey);
    callback?.();
}

/**
 * 设置行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {TableRow} record
 * @param {DataIndex} dataIndex
 */
export function setRowByIndex(id, record, dataIndex) {
    let store = getStore.call(this, id),
        row = store.getRows()?.[Number(dataIndex)],
        /** @detail 如果行状态是原始态，则置为新增态 */
        status = (!record.status || record.status === ROW_STATUS.ORIGIN) ? ROW_STATUS.EDIT : record.status,
        /** @detail values与旧行的合并 */
        values = { ...row.values, ...record.values },
        newRow = { ...row, ...record, values, status };

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId).setRowData(newRow);
    });
    updateFormData.call(this, id);

    // Object.keys(values).forEach(colKey => saveChangedRowsOldValue.call(this, id, row.key, colKey, values[colKey].value ));
}

/**
 * 通过rowid设置行编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey | RowKey[]} rowKeys
 * @param {boolean} enable - 是否可用
 */
export function setEditableRowByRowId(id, rowKeys, enable) {
    let store = getStore.call(this, id);

    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

    rowKeys.forEach(rowKey => store.setRowProps(rowKey, { disabled: !enable }));
}

/**
 * 通过dataIndex设置行编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {boolean} enable - 是否可用
 */
export function setEditableRowByIndex(id, dataIndexes, enable) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    let rowKeys = dataIndexes.map(dataIndex => rows[dataIndex].key);
    /** @detail 内部调用setEditableRowByRowId */
    setEditableRowByRowId.call(this, id, rowKeys, enable);
}

/**
 * 通过rowid设置单元格的编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey[]} rowKeys - 需要设置编辑性的行
 * @param {ColKey[]} [colKeys] - 需要设置编辑性的列，如未设置，则设置所有列的编辑性
 * @param {boolean} enable
 * @param {boolean} shouldForceUpdate
 */
export function setEditableRowKeyByRowId(id, rowKeys, colKeys, enable, shouldForceUpdate = true) {
    let store = getStore.call(this, id);

    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);
    !Array.isArray(colKeys) && (colKeys = [colKeys]);

    if (!colKeys) {
        /** @detail 如果没传ColKey 则更新整行的编辑性。此时内部调用setEditableRowByRowId */
        setEditableRowByRowId.call(this, id, rowKeys, enable, shouldForceUpdate);
        return;
    }

    rowKeys.forEach(rowKey =>
        colKeys.forEach(colKey =>
            store.setCellProps(rowKey, colKey, { disabled: !enable }),
        ),
    );
}

/**
 * 设置单元格属性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {Object} props
 */
export function setCellProps(id, rowKey, colKey, props) {
    let store = getStore.call(this, id);
    store.setCellProps(rowKey, colKey, props);
}

/**
 * 过滤出满足条件的数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys
 * @param {string} value
 * @returns {{ areaType: "table", rows: Array, areacode: null }} - 返回格式不对，这个方法估计没人用
 */
export function getFilterRows(id, keys, value) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    if (typeof value === 'undefined' || value === '') {
        rows = rows.map(row => ({ ...row, filterIndex: row.dataIndex }));
        /** @detail value为空时返回所有数据 */
        return {
            areaType: "table",
            rows: JSON.parse(JSON.stringify(rows)),
            areacode: null,
        };
    }

    !Array.isArray(keys) && (keys = [keys]);

    // store.setCheckedKeys();

    rows = rows.map(row => {
        if (row.status === ROW_STATUS.DELETE) { return row; }

        return {
            ...row,
            ...(keys.map(key => {
                let cellValue = store.getCellValue(row.key, key),
                    itemType = store.getColumnProps(key, 'itemtype');

                if (itemType === 'switch' || itemType === 'checkbox_switch' || itemType === 'switch_browse' || itemType === 'switch') {
                    value = typeof value === 'boolean' ? value : (value === 'Y' || value === 'true') ? true : false;
                    return cellValue?.value === value ? { filterIndex: row.dataIndex } : {};
                }

                return cellValue?._display_?.includes(value) ? { filterIndex: row.dataIndex } : {};
            })),
        };
    });

    /** @detail value有值时，返回的是空数组（与2111版本一直），所以这个方法估计没人用 */
    return {
        areaType: "table",
        rows: [],
        areacode: null,
    };
}

/**
 * 设置筛选条件
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys
 * @param {string} value - 筛选条件的值
 * @param {boolean} clearFilterCondition - 清空筛选条件。此时keys和value参数没用
 */
export function setFiltrateTableData(id, keys, value, clearFilterCondition) {
    let store = getStore.call(this, id),
        rows = store?.getRows();
    if (!store) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    store.setFilterInfo(); //清空筛选条件

    if (!value || clearFilterCondition) {
        rows.forEach(row => delete row.filter);
    } else {
        rows.forEach(row => {
            if (row.status === ROW_STATUS.DELETE) { return false; }
            row.filter = true;

            for (let i = 0; i < keys.length; i++) {
                let colKey = keys[i],
                    { display, value: colValue } = row.values[colKey],
                    { itemtype: itemType, languageMeta } = store.getColumnProps(colKey);

                if (!row.filter) { break; }

                if (itemType === 'residtxt' && Array.isArray(languageMeta)) {
                    for (let i = 0; i < languageMeta.length; i++) {
                        let { index, languageCode } = languageMeta[i],
                            realCode = colKey;
                        if (languageCode === loginLanguage) {
                            // 登录语言值
                            realCode = index == 1 ? colKey : colKey + index;
                        }
                        let cellValue = row.values[realCode];
                        if (cellValue.display?.includes(value) || cellValue.value?.includes(value)) {
                            row.filter = false;
                            continue;
                        }
                    }
                } else {
                    if (typeof display === 'string' && display.includes(value)) {
                        row.filter = false;
                    }
                    if (typeof colValue === 'string' && colValue.includes(value)) {
                        row.filter = false;
                    }
                    if (typeof colValue === 'boolean') {
                        row.filter = display === value || colValue === value;
                    }
                }
            }
        });
    }
    store.setRows({ rows });
}

/**
 * 移动行的方法
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} start
 * @param {DataIndex} end
 */
export function moveRow(id, start, end) {
    let store = getStore.call(this, id);

    store.moveRow(start, end);
}

/**
 * 设置行的状态
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {"0" | "1" | "2" | "3"} status
 */
export function setRowStatus(id, dataIndexes, status) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        deletedIndex = [];

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    dataIndexes.forEach(index => {
        rows[index].status = status;
        if (status === ROW_STATUS.DELETE) {
            deletedIndex.push(index);
        }
    });

    deletedIndex.length && delRow.call(this, id, deletedIndex);
}

/**
 * 获取行的状态
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @returns {"0" | "1" | "2" | "3"}
 */
export function getRowStatus(id, dataIndex) {
    let store = getStore.call(this, id);
    let rows = store.getRows();

    // rows = rows.filter(row => row.status !== ROW_STATUS.DELETE);
    return rows[dataIndex].status;
}

/**
 * 获取点击行
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{ record: TableRow, index: dataIndex }}
 */
export function getClickRowIndex(id) {
    let store = getStore.call(this, id);

    let rowKey = store.getClickedRow(),
        row = store.getRowData(rowKey);

    if (!rowKey) { return; }

    return { record: row, index: row.dataIndex }; // filterIndex
}

/**
 * 设置点击行
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {{ index: DataIndex }} data
 */
export function setClickRowIndex(moduleId, data) {
    let store = getStore.call(this, moduleId);
    let rows = store.getRows();
    let row = rows[data.index];

    store.setClickedRow(row.key);
}

/**
 * 设置行位置
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} start
 * @param {"up" | "down"} position
 */
export function setRowPosition(id, start, position) {
    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId);
        if (position === 'up') {
            store.moveRow(start, 0);
        } else if (position === "down") {
            let rows = store.getRows();
            // rows = rows.map(row => row.status !== ROW_STATUS.DELETE);
            store.moveRow(start, rows.length - 1);
        }
    });
}

/**
 * 多行数据插入到index行后
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {TableRow | TableRow[]} addRows
 * @param {DataIndex} dataIndex
 * @param {function} callback
 */
export function insertRowsAfterIndex(id, addRows, dataIndex, callback) {
    dataIndex = dataIndex === undefined ? 0 : dataIndex;

    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(addRows) && (addRows = [addRows]);

    addRows = addRows.map(row => {
        let rowKey = generateKey();
        // Object.entries(row.values).forEach(([colKey, value]) => value && saveChangedRowsOldValue.call(this, id, rowKey, colKey, value));
        return { ...row, key: rowKey };
    });

    if (!rows?.length || dataIndex < 0) {
        /** @detail 没数据，或者dataIndex < 0时，插入到0位置 */
        dataIndex = 0;
    } else if (dataIndex > rows.length) { // 越界默认插入最后一条
        /** @detail dataIndex > rows.length时，插入到row.length位置 */
        dataIndex = rows.length;
    } else { // 正常情况+1
        /** @detail 其余情况下插入到dataIndex + 1位置 */
        dataIndex = dataIndex + 1;
    }

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.addRow(dataIndex, addRows, { autoFocus: false });
    });

    /** @detail 设置表格状态为编辑态 */
    store.setStatus('edit');
    callback?.();
}

/**
 * 更新行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] } | Array<{ index: DataIndex, data: TableRow }>} records
 * @param {boolean} shouldForceUpdate
 */
export function updateRows(id, records, shouldForceUpdate = true) {
    let store = getStore.call(this, id),
        rows = [];

    /** @detail 此方法是全量更新，会替换旧行数据 */
    /** @detail 行状态为原始态时，更新行状态为编辑态 */
    if (records.rows) {
        // 2种情况，1. records: {rows}
        rows = records.rows.map(newRow => ({ ...newRow, status: !newRow.status || newRow.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : newRow.status }));
    } else if (Array.isArray(records)) {
        // 2种情况，2. records: [{index, data}]
        rows = records.map(record => {
            let { index: dataIndex, data: newRow } = record;

            let oldRow = store.getRows()?.[dataIndex];

            return {
                ...newRow,
                status: !newRow.status || newRow.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : newRow.status,
                key: oldRow.key,
            };
        });
    }

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.setRowData(rows, { shouldUpdate: shouldForceUpdate });
    });
    updateFormData.call(this, id);
}

/**
 * 更新行的属性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] } | Array<{ data: TableRow }>} data
 * @param {RowProps} props
 * @param {boolean} shouldForceUpdate
 */
export function updateRowsProps(id, data, props, shouldForceUpdate = true) {
    if (!data) { return; }

    let rows = [];

    if (data.rows) {
        rows = data.rows.map(row => ({ ...row, ...props }));
    } else if (Array.isArray(data)) {
        rows = data.map(row => ({ ...row.data, ...props }));
    }

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.setRowData(rows, { shouldUpdate: shouldForceUpdate });
    });

    updateFormData.call(this, id);
}

/**
 * 更新缓存为当前数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} shouldForceUpdate
 */
export function resetTableCaches(id, shouldForceUpdate = true) {
    let store = getStore.call(this, id),
        rows = getAllRows.call(this, id);

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.setCache('base', { rows, ...store.getAllRowsInfo(), ...store.getPaginationInfo() });
    });
    // rows.forEach(row => {
    //     Object.keys(row.values).forEach(colKey => {
    //         saveChangedRowsOldValue.call(this, id, row.key, colKey, row.values[colKey].value);
    //     });
    // });
    shouldForceUpdate && store.forceUpdate();
}

/**
 * 通过index更新行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Array<{ data: TableRow, index: DataIndex }>} records
 * @param {boolean} isCache
 * @param {boolean} isDel - 2207未实现
 */
export function updateDataByIndexs(id, records, isCache = false, isDel = false) {
    let store = getStore.call(this, id);

    !Array.isArray(records) && (records = [records]);

    let rows = [];

    records.forEach(({ data: row, index: dataIndex }) => {
        let rawRow = store.getRows()?.[dataIndex], values = {};

        if (!rawRow) { return; }

        Object.keys(row.values).forEach(colKey => {
            let { value, display, scale } = row.values[colKey];
            // 考虑undefined和null的情况，确保覆盖原值
            values[colKey] = {
                value: value,
                display: display,
                /** @detail scale默认取原单元格的scale */
                scale: typeof scale === 'undefined' ? store.getCellValue(rawRow.key, colKey)?.scale : scale,
            };
        });
        /** @detail 行状态为原始态时，更新为修改态 */
        /** @detail 增量更新values */
        rows.push({
            ...rawRow,
            ...row,
            values: { ...rawRow.values, ...values }, //避免values少属性
            status: typeof row.status === 'undefined' || row.status == ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
        });
    });

    execWithShare.call(this, id, shareId => {
        let tableStore = getStore.call(this, shareId);
        tableStore?.setRowData(rows);

        if (isCache) {
            tableStore.setCache('base', {
                rows: tableStore.getRows(),
                ...tableStore.getAllRowsInfo(),
                ...tableStore.getPaginationInfo(),
            });
        }
    });

    updateFormData.call(this, id);
}

/**
 * 获取表格某列数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @returns {{ value: Array, display: Array }}
 */
export function getColValue(id, colKey) {
    let store = getStore.call(this, id);
    let rows = store.getRows();
    let value = [], display = [];

    rows.forEach(row => {
        let cellData = store.getCellValue(row.key, colKey);
        value.push(cellData.value);
        cellData.display && display.push(cellData.display);
    });

    return { value, display };
}

/**
 * 设置表格某列数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 * @param {boolean} isSetEdit - 当行状态是原始态时，是否将行设置为编辑态
 */
export function setColValue(id, colKey, cellValue, isSetEdit = true) {
    let store = getStore.call(this, id);
    let rows = store.getRows();

    rows.forEach(row => {
        /** @detail 已删除的行不做处理 */
        if (row.status === ROW_STATUS.DELETE) { return; }

        if (isSetEdit && row.status === ROW_STATUS.ORIGIN) {
            row.status = ROW_STATUS.EDIT;
        }

        // cellValue.value && saveChangedRowsOldValue.call(this, id, row.key, colKey, cellValue.value);

        /** @detail cellValue是增量更新 */
        row.values[colKey] = {
            ...row.values[colKey],
            ...cellValue,
        };

        execWithShare.call(this, id, shareId => {
            getStore.call(this, shareId)?.setCellValue(row.key, colKey, { value: row.values[colKey], changeStatus: isSetEdit });
        });
        updateFormData.call(this, id);
    });

}

/**
 * 设置某些表格某些列的精度
 * @memberof EditTable
 * @param {Array<{ areacode: ModuleId, fieldcode: ColKey, scale:number }>} params
 */
export function setColScale(params) {
    params.forEach(param => {
        const { areacode: id, fieldcode: colKey, scale } = param;
        setColValue.call(this, id, colKey, { scale });
    });
}

/**
 * 隐藏列
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey | ColKey[]} keys
 */
export function hideColByKey(id, keys) {
    let store = getStore.call(this, id);

    if (typeof keys !== 'string' && !Array.isArray(keys)) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    // TODO:EditTable不需要这段逻辑吧，EditTable没有formId
    if (store) {
        let rows = store.getRows();
        keys.forEach(colKey => store.setColumnProps(colKey, { visible: false }, { shouldUpdate: false }));
        rows.forEach(row => keys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: false })));
        store.forceUpdate();
    } else if (this.formDataSource[id]) {
        keys.forEach(key => {
            this.formDataSource[id]?.setItemAttribute(key, 'visible', false);
        });
    } else if (this.state.meta[id]) {
        this.state.meta[id].items.forEach(col => {
            if (keys.includes(col.attrcode) && col.visible) {
                col.visible = false;
            }
        });
        this.meta.setMeta(this.state.meta);
    }
}

/**
 * 显示列
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey | ColKey[]} keys
 */
export function showColByKey(id, keys) {
    let store = getStore.call(this, id);

    if (typeof keys !== 'string' && !Array.isArray(keys)) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    // TODO:EditTable不需要这段逻辑吧，EditTable没有formId
    if (store) {
        let rows = store.getRows();
        keys.forEach(colKey => store.setColumnProps(colKey, { visible: true }, { shouldUpdate: false }));
        rows.forEach(row => keys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: true })));
        store.forceUpdate();
    } else if (this.formDataSource[id]) {
        keys.forEach(key => {
            this.formDataSource[id]?.setItemAttribute(key, 'visible', true);
        });
    } else if (this.state.meta[id]) {
        this.state.meta[id].items.forEach(col => {
            if (keys.includes(col.attrcode) && (typeof col.visible == 'undefined' || col.visible == false)) {
                col.visible = true;
            }
        });
        this.meta.setMeta(this.state.meta);
    }
}

/**
 * 根据dataIndex设置单元格的值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 * @param {boolean} isSetEdit - 当行状态是原始态时，是否将行设置为编辑态
 * @param {boolean} isUpdateBatch
 */
export function setValByKeyAndIndex(id, dataIndex, colKey, cellValue, isSetEdit = true, isUpdateBatch = false) {
    let store = getStore.call(this, id);
    let row = store.getRows()?.[dataIndex];
    if (!row) { return; }
    /** @detail 内部调用setValByKeyAndRowId */
    setValByKeyAndRowId.call(this, id, row.key, colKey, cellValue, isSetEdit, isUpdateBatch);
}

/**
 * 根据rowid设置单元格的值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 * @param {boolean} isSetEdit - 当行状态是原始态时，是否将行设置为编辑态
 * @param {boolean} [isUpdateBatch=true] - 2207未实现
 */
export function setValByKeyAndRowId(id, rowKey, colKey, cellValue, isSetEdit = true, isUpdateBatch = true) {
    if (!rowKey) { return; }

    let store = getStore.call(this, id);

    let row = store.getRowData(rowKey),
        rawCellValue = store.getCellValue(row.key, colKey);
    isSetEdit && row.status === ROW_STATUS.ORIGIN && (store.status = ROW_STATUS.EDIT);

    execWithShare.call(this, id, shareId => {
        /** @detail 增量更新cellValue */
        getStore.call(this, shareId)?.setCellValue(rowKey, colKey, { value: { ...rawCellValue, ...cellValue }, shouldForceUpdate: false, changeStatus: isSetEdit });
    });
    updateFormData.call(this, id);

    // cellValue.value && saveChangedRowsOldValue.call(this, id, rowKey, colKey, cellValue.value);
}

/**
 * 更新附件值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {number} num  附件数量
 * @param {string} pkname  主键名
 * @param {string} pkValue  主键值
 */
export function updateAttachmentValue(moduleId, num, pkname, pkValue) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        cols = store.getVisibleColumns(),
        { rowId } = rows.find(row => row.values[pkname].value === pkValue) || {};
    let value = `${num},${pkValue}`;
    cols.forEach(col => {
        if (col.itemtype === "attachment") {
            execWithShare.call(this, moduleId, shareId => {
                /** @detail 增量更新cellValue */
                getStore.call(this, shareId)?.setCellValue(rowId, col.attrcode, { value: { value }, shouldForceUpdate: false });
            });
            updateFormData.call(this, moduleId);
        }
    });
}

/**
 * 根据dataIndex获取单元格的值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @returns {CellValue}
 */
export function getValByKeyAndIndex(id, dataIndex, colKey) {
    let store = getStore.call(this, id),
        row = store.getRows()?.[dataIndex];

    if (!row) { return {}; }

    /** @detail 内部调用getValByKeyAndRowId */
    return getValByKeyAndRowId.call(this, id, row.key, colKey);
}

/**
 * 根据rowid获取单元格的值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @returns {CellValue}
 */
export function getValByKeyAndRowId(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    let cellValue = store.getCellValue(rowKey, colKey);

    return JSON.parse(JSON.stringify(cellValue || {}));
}

/**
 * 通过rowid设置单元格的编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {boolean} enable
 */
export function setEditableByKey(id, rowKey, colKey, enable) {
    let store = getStore.call(this, id);
    store.setCellProps(rowKey, colKey, { disabled: !enable });
}
/**
 * 通过rowid获取单元格的编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function getEditableByKey(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    return !store.getCellProps(rowKey, colKey, 'disabled');
}

/**
 * 获取单元格属性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {string} attributeName
*/
export function getCellProps(id, rowKey, colKey, attributeName) {
    let store = getStore.call(this, id);
    return store.getCellProps(rowKey, colKey, attributeName);
}

/**
 * 获取列属性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @param {string} attributeName
*/
export function getColumnProps(id, colKey, attributeName) {
    let store = getStore.call(this, id);
    return store.getColumnProps(colKey, attributeName);
}

/**
 * 通过dataIndex设置单元格的编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {ColKey | ColKey[]} colKeys
 * @param {boolean} enable
 * @param {boolean} shouldForceUpdate
 */
export function setEditableRowKeyByIndex(id, dataIndexes, colKeys, enable, shouldForceUpdate = true) {
    let store = getStore.call(this, id);
    let rows = store.getRows();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);
    !Array.isArray(colKeys) && (colKeys = [colKeys]);

    // rows = rows.filter(row => row.status !== ROW_STATUS.DELETE);
    dataIndexes.forEach(dataIndex => {
        let row = rows[dataIndex];
        if (!row) { return; }

        /** @detail 内部调用setEditableByKey */
        colKeys.forEach(colKey => setEditableByKey.call(this, id, row.key, colKey, enable));
    });
}

/**
 * 获取某列显隐性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @returns {boolean} - 显示属性
 */
export function checkVisible(id, colKey) {
    let store = getStore.call(this, id);

    return store.getColumnProps(colKey, 'visible');
}

/**
 * 设置表格数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {TableData} data - 表格数据
 * @param {boolean} isCache - 是否缓存数据
 * @param {boolean} isTop - 2207未实现
 * @param {boolean} autoFocus - 是否自动聚焦到第一个元素
 */
export function setTableData(id, data, isCache = true, isTop, autoFocus = true, extraConfig = {}) {
    if (!getStore.call(this, id) || !data) { return; }

    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId),
            { pkname, crossPageSelect } = store?.getConfig() || {},
            { allpks, alltss } = store?.getAllRowsInfo() || {},
            lastAllpks = [...allpks];

        if (!store) { return; }

        !this.isUpdatePage && store.getInstance().preventUpdate(true);

        try {
            /** @detail 清除排序、筛选、行点击、行选中、行禁用、最后一次check等状态、组合排序 */
            // store.setSortInfo();
            // store.setFilterInfo();
            store.setClickedRow();
            store.setSelectedCell();
            store.setCopiedCell(); //清空复制单元格
            store.setLastCheckedKey();
            store.setDisabledKeys([], { shouldUpdate: false });
            store.setGroupSortInfo();

            // 恢复的情况：data没有携带pageInfo && data.rows长度大于0 && allpks(原来的)设置过
            if (data.hasOwnProperty('pageInfo') && ![undefined, null, ''].includes(data.pageInfo)) {
                store.setPaginationInfo(data.pageInfo);
            } else if (data?.rows?.length === 0 || !allpks?.length) {
                store.setPaginationInfo();
            }

            // 按条件设置allpks 和 选中行
            if (data.allpks) {
                allpks = data.allpks;
            } else if (data.rows && data.rows.length === 0) {
                allpks = [];
            }

            if (data.alltss) {
                alltss = data.alltss;
            } else if (data.rows && data.rows.length === 0) {
                alltss = [];
            }

            updateFormData.call(this, id);
            // store.setCache('oldRows', null); 
            if (extraConfig.disabledClearSort) {
                store.setSortInfo(store.getSortInfo());
            } else {
                store.setSortInfo();
            }
            if (extraConfig.disabledClearFilter) {
                store.setFilterInfo(store.getFilterInfo());
            } else {
                store.setFilterInfo();
            }

            /** @detail 设置表格数据 */
            store.setRows(data);
            store.setAllRowsInfo({ allpks, alltss });

            if (isCache) {
                store.setCache('base', {
                    rows: store.getRows(),
                    ...store.getAllRowsInfo(),
                    ...store.getPaginationInfo(),
                });
            }

            if (crossPageSelect && pkname) {
                /** @detail 跨页选时，尽可能保留选中状态 */
                let allCheckedKeys = store.getAllCheckedKeys(),
                    checkedKeys = new Set();

                //如果传了pk 并且所传pk和上一次不一样则清空所有跨页全选的数据
                if (data.allpks && data.allpks.length) {
                    let lastAllpksSet = new Set(lastAllpks);
                    if (data.allpks.length !== lastAllpks.length || data.allpks.some(pk => !lastAllpksSet.has(pk))) {
                        store.setAllCheckedKeys(new Set());
                        allCheckedKeys = new Set();
                    }
                }

                // 没传pk 并且rows [](对应上门的rows 为空 则allpk=[])
                if (!data.allpks && !allpks.length) {
                    store.setAllCheckedKeys(new Set());
                    allCheckedKeys = new Set();
                }

                if (store.isAllChecked()) {
                    store.setCheckedKeys(new Set(data.rows.map(row => row.key || row.rowid || row.rowId)), { isCheckedAll: true });
                    store.setAllCheckedKeys(new Set(data.allpks || allpks), { isCheckedAll: true });
                } else {
                    data?.rows?.forEach(row => {
                        if (row.values[pkname]) {
                            let pk = row.values[pkname].value;
                            if (allCheckedKeys.has(pk)) {
                                checkedKeys.add(row.key || row.rowid || row.rowId);
                            }
                        }
                    });
                    store.setCheckedKeys(checkedKeys);
                }
            } else {
                /** @detail 非跨页选时清空选中行 */
                store.setCheckedKeys(new Set());
            }

            if (isCache) {
                store.setCache('base', {
                    rows: store.getRows(),
                    ...store.getAllRowsInfo(),
                    ...store.getPaginationInfo(),
                });
            }
        } catch (e) {
            console.error(e);
        }

        !this.isUpdatePage && store.getInstance().preventUpdate(false);
        store.forceUpdate();
    });

    let store = getStore.call(this, id);
    if (autoFocus && store.getStatus() === 'browse' && data.rows.length) {
        (function autoFocus() {
            requestAnimationFrame(() => {
                // 聚焦第一个元素
                let leafColumnKeys = store.getVisibleLeafColumnKeys().filter(
                    key => isValidType(store.getColumnProps(key, 'itemtype')),
                );

                if (leafColumnKeys.length) {
                    store.focusOnCell(store.getRows()[0]?.key, leafColumnKeys[0]);
                } else {
                    // DOM还没出来（还没有meta的时候），继续轮询
                    autoFocus();
                }
            });
        })();
    }

    /** @detail 由于更新了行选中状态，所以会触发config.selectedChange回调 */
    let { selectedChange } = store.getConfig();
    selectedChange?.({ ...this.props, ...this.output }, id, 0);
}

/**
 * 供应链加： 设置表格数据(不更改分页和pk信息)
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {TableData} data -表格数据
 */
export function setOnlyTableData(moduleId, data) {
    let store = getStore.call(this, moduleId);
    if (!store || !data) { return; }
    let pageInfo = store?.getPaginationInfo(),
        { allpks, alltss, allbodypks, pkrelation } = store?.getAllRowsInfo();
    data = {
        ...data,
        pageInfo: pageInfo,
        pkrelation: pkrelation,
        allbodypks: allbodypks,
        allpks: allpks,
        alltss: alltss,
    };
    setTableData.call(this, moduleId, data);
}

/**
 * 取消编辑
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {function} callback
 */
export function cancelEdit(id, callback) {
    let store = getStore.call(this, id);

    /** @detail 将表格状态变更为浏览态 */
    store.setStatus('browse');
    /** @detail 清空页面的toast提示 */
    clearOneTypeToast('danger');
    /** @detail 清空校验状态 */
    clearRequiredStatus.call(this, id);

    let oldData = store.getCache('base');

    !oldData.rows && (oldData.rows = []);

    /** @detail 从缓存里取旧值，恢复到旧值 */
    setTableData.call(this, id, oldData, true, false, true, {
        disabledClearSort: true,
        disabledClearFilter: true,
    });
    // 旧表格的属性都存在cellValue里，当重新设置数据时会将属性恢复默认
    store.clearCellProps();
    callback?.();
}

/**
 * 清除校验状态
 * @memberof EditTable
 * @param {ModuleId} id
 */
export function clearRequiredStatus(id) {
    let store = getStore.call(this, id);

    store.clearValidate();
}

/**
 * 设置表格状态
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {"add" | "edit" | "browse"} status
 * @param {function} callback
 */
export function setStatus(id, status, callback) {
    let store = getStore.call(this, id),
        oldStatus = store?.getStatus();

    if (!store) { return; }

    /** @detail 切换到浏览态时，清空toast提示和校验状态 */
    if (oldStatus !== 'browse' && status === 'browse') {
        clearOneTypeToast('danger');
        clearRequiredStatus.call(this, id);
    }

    /** @detail 切换到浏览态时，清空用于批改的缓存值 */
    if (status === 'browse') {
        store.setCache('oldBatchData', undefined);
    }

    store.setStatus(status);

    /** @detail 调用config.statusChange */
    let { statusChange } = store.getConfig();
    statusChange?.(status);

    typeof callback === 'function' && callback(id, status, store.getCache('base'), store);
}

/**
 * 获取表格状态
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {"add" | "edit" | "browse"}
 */
export function getStatus(id) {
    let store = getStore.call(this, id);

    /** @detail 默认为'browse' */
    return store?.getStatus() || 'browse';
}

/**
 * 获取变化行信息
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {TableRow[]}
 */
export function getChangedRows(id) {
    let store = getStore.call(this, id);
    let rows = store.getRows();

    /** @detail 返回非原始态的数据 */
    return rows.filter(row => row.status !== ROW_STATUS.ORIGIN).map(row => ({ rowid: row.key, status: row.status, values: row.values }));
}

/**
 *  复制粘贴行，默认粘贴到该行下方
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {TableRows[]} data
 * @param {DataIndex} dataIndex
 */
export function pasteRow(id, data, dataIndex) {
    let store = getStore.call(this, id);

    if (dataIndex < 0) {
        dataIndex = 0;
    } else if (dataIndex === undefined) {
        dataIndex = 0;
    } else {
        let rows = store.getRows();
        // rows = rows.filter(row => row.status !== ROW_STATUS.DELETE);

        let row = rows[dataIndex];
        dataIndex = row.dataIndex + 1;
    }

    let addRow = { ...(JSON.parse(JSON.stringify(data))), key: generateKey() };

    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.addRow(dataIndex, addRow, { autoFocus: false });
    });

    /** @detail 设置表格状态为编辑态 */
    store.setStatus('edit');
}

/**
 * 获取缓存数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {TableData}
 */
export function getCacheDataById(id) {
    let store = getStore.call(this, id);
    return store.getCache('base') || {};
}

/**
 * 获取拖拽后的列宽
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {Object}
 */
export function getAllColumnsWidth(id) {
    let store = getStore.call(this, id);
    return store.getColumnsWidth();
}

/**
 * 2207未实现
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {object}
 */
export function getCacheForEditModal(id) {
    let store = getStore.call(this, id);
    return { rows: store.getCache('beforeEdit') };
}

/**
 * 设置某列的编辑性
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {ColKey[]} colKeys
 * @param {boolean} disabled
 */
export function setColEditableByKey(id, colKeys, disabled) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(colKeys) && (colKeys = [colKeys]);

    colKeys.forEach(colKey => store.setColumnProps(colKey, { disabled }, { shouldUpdate: false }));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, { disabled })));

    store.forceUpdate();
}

/**
 * 通过rowid更新表格数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] }} data
 * @param {function} callback
 * @param {boolean} isCache
 */
export function updateTableData(id, data, callback, isCache = true) {
    if (!data || !Array.isArray(data.rows)) { return; }

    let store = getStore.call(this, id),
        rows = data.rows.filter(row => store.getRowData(row.rowid || row.rowId || row.key));

    execWithShare.call(this, id, shareId => {
        let tableStore = getStore.call(this, shareId);
        if (!tableStore) { return; }

        /** @detail values是增量更新 */
        tableStore.setRowData(rows);
        updateFormData.call(this, id);
        isCache && tableStore.setCache('base', {
            rows: tableStore.getRows(),
            ...tableStore.getAllRowsInfo(),
            ...tableStore.getPaginationInfo(),
        });
    });

    // rows.forEach(row =>
    //     Object.keys(row.values).forEach(colKey =>
    //         saveChangedRowsOldValue.call(this, id, row.key, colKey, row.values[colKey].value),
    //     ),
    // );

    callback?.();
}

/**
 * 校验某些行的数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {TableRow[]} rows
 * @param {boolean} autoFocus
 * @returns {boolean} - 是否校验通过
 */
export function checkRequired(id, rows, autoFocus = true) {
    if (!Array.isArray(rows)) { return false; }

    let store = getStore.call(this, id),
        rowToast = [],
        columnKeys = store.getVisibleLeafColumnKeys()
            .filter(colKey => store.getColumnProps(colKey, 'required')),
        focusColKey,
        locale = store.getLocale();

    // 1. 拿到所有需要验证的且可见的列
    // 2. 所有可见数据对每一列验证数据
    rows.forEach((row, index) => {
        if (row.status === ROW_STATUS.DELETE) { return; }
        let errorLabels = [];

        columnKeys.forEach(colKey => {
            let rowKey = row.rowid || row.rowId || row.key,
                cellValue = store.getCellValue(rowKey, colKey);

            if ([undefined, null, ''].includes(cellValue?.value)) {
                errorLabels.push(store.getColumnProps(colKey, 'label'));
                store.setCellProps(rowKey, colKey, { invalid: true });
                !focusColKey && (focusColKey = colKey);

            } else {
                store.setCellProps(rowKey, colKey, { invalid: false });
            }
        });

        if (errorLabels.length) {
            if (autoFocus) {
                autoFocus = false;
                store.focusOnCell(row.key || row.rowid, focusColKey);
            }
            rowToast.push(`${locale['table_first'] /*第*/}${index + 1}${locale['table_row'] /*行*/}`.concat(errorLabels.join('，')).concat('；'));
        }
    });

    if (rowToast.length) {
        toast({
            content: `${locale['table_no_empty'] /*以下字段不能为空*/}`,
            color: 'danger',
            TextArr: [locale['table0022' /*展开*/], locale['table0023' /*收起*/], locale['table0024' /*关闭*/]],
            groupOperation: true,
            groupOperationMsg: rowToast,
        });
    }
    return rowToast.length === 0;
}

/**
 * 整表校验
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ customRules: function, autoFocus: boolean }} config
 * @returns {object}
 */
export function checkTableRequired(id, { customRules, autoFocus = true, callback } = {}) {
    let store = getStore.call(this, id),
        validateInfo = store.validate(customRules),
        requiredItems = {}, // 存储错误信息 {rowKey: [colKey]}
        rowToast = '',
        locale = store.getLocale(),
        editTableTitle = `${locale['table_no_empty'] /*以下字段不能为空*/}\n`,
        editTableDetailTitle = [],
        requiredItemsKeyVal = [],
        tableName = '';

    validateInfo.forEach((colKeys, rowKey) => {
        let errModel = [];
        let row = store.getRowData(rowKey),
            labels = [...colKeys].map(colKey => `${store.getColumnProps(colKey, 'label')}`);
        tableName = store?.getMeta()?.[id].name;
        [...colKeys].forEach((colKey, index) => {
            requiredItemsKeyVal.push({ colKey: colKey, rowKey: rowKey, display: `${store.getColumnProps(colKey, 'label')}`, area: id, rowIndex: row.dataIndex + 1 });
        });
        labels.forEach((label, index) => {
            errModel.push('${field' + (index + 1) + '}');
        });
        requiredItems[rowKey] = [...colKeys];
        let txt = locale['table_first'/*第*/] + (row.dataIndex + 1) + `${locale['table_row']/*行*/}`.concat(errModel.join('、'));
        editTableDetailTitle.push({ [rowKey]: txt });
        rowToast = rowToast.concat(locale['table_first'/*第*/] + (row.dataIndex + 1) + `${locale['table_row']/*行*/}`).concat(errModel.join('、')).concat(';\n');
    });

    let passed = Object.keys(requiredItems).length === 0;

    if (!passed && autoFocus) {
        let firstRowKey = Object.keys(requiredItems)[0];
        let firstColumnKey = requiredItems[firstRowKey][0];
        if (typeof callback === 'function') {
            callback({ moduleId: id, rowKey: firstRowKey, attrcode: firstColumnKey });
        } else {
            store.focusOnCell(firstRowKey, firstColumnKey);
        }
    }

    return {
        requiredItems,
        regExpItem: [],
        maxItems: [],
        content: `${locale['table_no_empty'] /*以下字段不能为空*/}\n`,
        editTableTitle: editTableTitle,
        editTableDetailTitle: editTableDetailTitle,
        requiredItemsKeyVal: requiredItemsKeyVal,
        passed,
        tableName,
    };

}

/**
 * 根据rowid更新行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ rows: TableRows[] }} data
 */
export function updateDifference(id, data) {
    if (!Array.isArray(data?.rows)) { return; }

    let store = getStore.call(this, id),
        rows = data.rows.filter(row => row.values && store.getRowData(row.rowid || row.rowId || row.key))
            .map(row => {
                // let values = row.values;

                // Object.keys(values).forEach(colKey =>
                //     saveChangedRowsOldValue.call(this, id, row.rowid || row.rowId || row.key, colKey, values[colKey]?.value),
                // );

                return {
                    ...row,
                    status: (typeof row.status == 'undefined' || row.status == ROW_STATUS.ORIGIN) ? ROW_STATUS.EDIT : row.status,
                };
            });
    execWithShare.call(this, id, shareId => {
        getStore.call(this, shareId)?.setRowData(rows);
    });
    updateFormData.call(this, id);
}

/**
 * 更新表格
 * @memberof EditTable
 * @param {ModuleId} id
 */
export function updateTable(id) {
    let store = getStore.call(this, id);
    // rows = store.getRows(),
    // filterInfo = store.getFilterInfo();

    // Array.isArray(rows) && rows.forEach(row => {
    //     Object.keys(row.values).forEach(colKey => {
    //         if(row.values[colKey].value) {
    //             saveChangedRowsOldValue.call(this, id, row.key, colKey, row.values[colKey].value);
    //         }
    //     });
    // });

    // store.setFilterInfo(filterInfo);
    store.forceUpdate();
}

/**
 * 设置过滤条件
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setFilterInfo(id, info) {
    let store = getStore.call(this, id);
    store?.setFilterInfo(info);
}

/**
 * 获取过滤条件
 * @memberof EditTable
 * @param {ModuleId} id
 */
export function getFilterInfo(id) {
    let store = getStore.call(this, id);
    return store?.getFilterInfo();
}

/**
 * 设置排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setSortInfo(id, info) {
    let store = getStore.call(this, id);
    store?.setSortInfo(info);
}

/**
 * 获取排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 */
export function getSortInfo(id) {
    let store = getStore.call(this, id);
    return store?.getSortInfo();
}

/**
 * 设置某些行某些列的编辑性 - 原方法有问题，估计没人用
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Array<{ keys: ColKey[] }>} data
 */
export function setEditableKeyEdit(id, data) {
    let store = getStore.call(this, id);
    let rows = store.getRows();
    if (!Array.isArray(rows) || !Array.isArray(data)) { return; }

    data.forEach(newRowProps => {
        const { keys } = newRowProps;
        Object.keys(keys).forEach(colKey => {
            store.setCellProps(newRowProps.rowid || newRowProps.rowId || newRowProps.key, colKey, { disabled: !keys[colKey] });
        });
    });
}

/**
 * 更新表格高度 - 2207已废弃
 * @memberof EditTable
 */
export function updateTableHeight() {
}

/**
 * 更新行数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] }} data
 * @param {boolean} isCache
 * @param {boolean} isDeepDel - 是否将删除的数据真删
 */
export function updateDataByRowId(id, data, isCache = false, isDeepDel = true) {
    let store = getStore.call(this, id);

    if (!data?.rows?.length) { return; }
    let rows = data.rows.map(row => {
        let rowKey = row.rowid || row.rowId || row.key,
            rawRow = store.getRowData(rowKey),
            values = {};

        if (!rawRow) { return; }

        Object.keys(row.values).forEach(colKey => {
            let { value, display, scale } = row.values[colKey];
            // 考虑undefined和null的情况，确保覆盖原值
            /** @detail cellValue是全量更新 */
            values[colKey] = {
                value: value,
                display: display,
                scale: typeof scale === 'undefined' ? store.getCellValue(rawRow.key, colKey)?.scale : scale,
            };

            // saveChangedRowsOldValue.call(this, id, row.rowKey, colKey, row.values[colKey]);
        });

        /** @detail values是增量更新 */
        return {
            ...rawRow,
            ...row,
            status: typeof row.status === 'undefined' || row.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
            values: { ...rawRow.values, ...values }, //避免values少属性
        };
    }).filter(e => !!e);

    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId);
        if (!store) { return; }

        store.setRowData(rows);
        updateFormData.call(this, id);

        let allRows = store.getRows();
        if (isDeepDel) {
            store.setRows({ rows: allRows.filter(e => e.status !== ROW_STATUS.DELETE) });
            updateFormData.call(this, id);
            allRows = store.getRows();
        }

        if (isCache) {
            store.setCache('base', {
                rows: allRows,
                ...store.getAllRowsInfo(),
                ...store.getPaginationInfo(),
            });
        }
    });
}

/**
 * 获取选中行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} isDeepCopy
 * @param {string} view  - 2207未实现
 * @param {boolean} isViewSort  按视图顺序返回结果 默认是按勾选顺序
 * @returns {Array<{ data: TableRow, index: DataIndex } | { data: TableRow, index: DataIndex, pageIndex: number }>}
 */
export function getCheckedRows(id, isDeepCopy = true, isViewSort) {
    let store = getStore.call(this, id),
        checkedKeys = store?.getCheckedKeys(),
        allCheckedKeys = store?.getAllCheckedKeys(),
        rows = store?.getRows();

    if (!checkedKeys || !rows || !store) { return; }

    let { crossPageSelect, pkname } = store.getConfig(),
        isEditStatus = store.getStatus() === 'browse' ? false : true,
        pageInfo = store.getPaginationInfo(),
        allInfo = store.getAllRowsInfo(),
        checkedRows = [];

    if (crossPageSelect && pkname && !isEditStatus) {
        /** @detail 跨页选时，当页选中的返回TableRow，非当页选中的返回 { pk, ts } */
        let currentPageRowKeys = new Map(rows.map(row => [row.values[pkname].value, row.key])),
            pkIndexes = {};
        if (isViewSort) {
            let currentPagePk = rows.map(row => row.values[pkname].value);
            let sortAllPk = [...allInfo.allpks];
            let { pageSize, pageIndex } = pageInfo;
            sortAllPk.splice(pageSize * pageIndex, pageSize, ...currentPagePk);
            sortAllPk.forEach((pk, index) => pkIndexes[pk] = index);
        } else {
            allInfo.allpks.forEach((pk, index) => pkIndexes[pk] = index);
        }

        [...allCheckedKeys].sort((pkA, pkB) => pkIndexes[pkA] - pkIndexes[pkB]).forEach(pk => {
            if (currentPageRowKeys.has(pk)) {
                let row = store.getRowData(currentPageRowKeys.get(pk));
                if (!row) { return; }

                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                    pageIndex: pageInfo?.pageIndex,
                });
            } else {
                let pkIndex = pkIndexes[pk];
                if (pkIndex === -1) { return; }
                checkedRows.push({
                    data: { pk, ts: allInfo.alltss?.[pkIndex] || '' },
                    index: pkIndex % pageInfo.pageSize,
                    pageIndex: Math.floor(pkIndex / pageInfo.pageSize),
                });
            }
        });
    } else {
        /** @detail 非跨页选时，返回TableRow */
        checkedKeys = new Set([...checkedKeys].sort((rowKeyA, rowKeyB) =>
            store.getRowData(rowKeyA) && store.getRowData(rowKeyB) ? store.getRowData(rowKeyA).viewIndex - store.getRowData(rowKeyB).viewIndex : 0,
        ));
        // rows = store.getVisibleRows(); // 和之前保持一致，取当前视图数据，不然索引不对（比如业务方会拿选中数据的索引在更新合计时用到，此时如果处于筛选状态，索引对不上）
        rows.forEach(row => {
            if (checkedKeys.has(row.key)) {
                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                });
            }
        });
    }
    return isDeepCopy ? JSON.parse(JSON.stringify(checkedRows)) : checkedRows;
}

/**
 * 全选/取消全选所有行
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} checked
 */
export function selectAllRows(id, checked) {
    execWithShare.call(this, id, share => {
        let store = getStore.call(this, share);
        if (checked) {
            store.checkAll();
        } else {
            store.setCheckedKeys(new Set());
            store.setAllCheckedKeys(new Set());
        }
    });

    /** @detail 触发config.selectedChange */
    let { selectedChange } = getStore.call(this, id)?.getConfig() ?? {};
    typeof selectedChange === 'function' && selectedChange({ ...this.prop, ...this.output }, id, getStore.call(this, id).getCheckedKeys().size);
}

/**
 * 反选
 * @memberof EditTable
 * @param {ModuleId} id
 */
export function reverseSelected(id) {
    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId);
        store.checkReverse();
    });

    /** @detail 触发config.selectedChange */
    let { selectedChange } = getStore.call(this, id)?.getConfig() ?? {};
    typeof selectedChange === 'function' && selectedChange({ ...this.prop, ...this.output }, id, getStore.call(this, id).getCheckedKeys().size);
}

/**
 * 打开弹窗编辑
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {'edit' | 'add' | 'browse'} status
 * @param {TableRow} record - 2207未实现
 * @param {DataIndex} dataIndex
 * @param {boolean} autoFocus - 2207未实现
 */
export function openModel(id, status, record, dataIndex, autoFocus) {
    let store = getStore.call(this, id);

    store?.openSideEdit(status, dataIndex);

}

/**
 * 关闭弹窗编辑
 * @memberof EditTable
 * @param {ModuleId} id
 */
export function closeModel(id) {
    let store = getStore.call(this, id);
    store?.closeSideEdit();
}

/**
 * 批量设置整列数据（要先选中某个单元格）
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{ code: ColKey, value, display }}
 */
export function batchChangeTableData(id) {
    let store = getStore.call(this, id),
        batchData = store.getCache('oldBatchData', { strict: true }), //记一下最后一次click的cell ,rowKey colKey
        rows = store.getRows(),
        value = {};

    if (!batchData || !rows?.length) { return; }

    value = store.getCellValue(rows?.[batchData.dataIndex]?.key, batchData.colKey);

    /** @detail 删除的行不处理 */
    rows = rows.map(row => row.status === ROW_STATUS.DELETE ? row : {
        ...row,
        status: row.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
        values: {
            ...row.values,
            [batchData.colKey]: { ...value },
        },
    });

    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setRows({ rows }));

    updateFormData.call(this, id);

    return {
        code: batchData.colKey,
        value: value.value,
        display: value.display,
    };
}

/**
 * 设置复选框是否禁用
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {boolean} enable
 * @returns {DataIndex[]}
 */
export function setCheckboxDisabled(id, dataIndexes, enable) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        disabledKeys = store.getDisabledKeys();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    let keys = dataIndexes.map(dataIndex => rows[dataIndex].key);

    keys.forEach(key => enable ? disabledKeys.delete(key) : disabledKeys.add(key));

    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setDisabledKeys(disabledKeys));

    return dataIndexes.every(dataIndex => dataIndex < rows.length && dataIndex > 0);
}

/**
 * 设置所有行复选框是否禁用
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} enable
 */
export function setAllCheckboxAble(id, enable) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        disabledKeys = new Set();

    if (!enable) {
        disabledKeys = new Set(rows.map(row => row.key));
    }

    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setDisabledKeys(disabledKeys));
    return false;
}

/**
 * 设置行选中
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {boolean} selected  true增加选中行(保留之前的) false取消已选
 */
export function selectTableRows(id, dataIndexes, selected) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    let checkedKeys = dataIndexes.map(dataIndex => rows[dataIndex].key);

    let keys = store.getCheckedKeys();
    if (selected) {
        keys = new Set([...keys, ...checkedKeys]);
    } else {
        checkedKeys = new Set(checkedKeys);
        keys = new Set([...keys].filter(key => !checkedKeys.has(key)));
    }

    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setCheckedKeys(keys));
}

/**
 * 通过pkname的值设置行选中
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Array} pks
 */
export function selectTableRowsByPkName(id, pks) {
    let store = getStore.call(this, id),
        { pkname, crossPageSelect } = store?.getConfig() ?? {};

    if (!pkname || !crossPageSelect || !store) { return; }

    !Array.isArray(pks) && (pks = [pks]);
    let rows = store.getRows(),
        pkSet = new Set(pks),
        checkedKeys = rows.filter(row => pkSet.has(row.values[pkname]?.value)).map(row => row.key);

    store.setCheckedKeys(new Set(checkedKeys));
    store.setAllCheckedKeys(new Set(pks));
}

/**
 * 获取最后一个操作过的单元格的信息（大概是和batchChangeTableData配合）
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{ batchChangeIndex: DataIndex, batchChangeKey: ColKey, batchChangeValue: string | boolean, batchChangeDisplay: string }}
 */
export function getTableItemData(id) {
    let store = getStore.call(this, id),
        batchData = store.getCache('oldBatchData') || {},
        { colKey, dataIndex } = batchData,
        rows = store.getRows(),
        rowKey = rows?.[dataIndex]?.key,
        itemtype = colKey && store.getColumnProps(colKey, 'itemtype'),
        cellValue = rowKey && colKey && store.getCellValue(rowKey, colKey);

    if (!rowKey) {
        return { batchChangeKey: '' };
    }

    return {
        batchChangeIndex: dataIndex,
        batchChangeKey: colKey,
        batchChangeValue: cellValue?.value,
        batchChangeDisplay: itemtype === 'residtxt' ? null : cellValue?.display,
    };
}

/**
 * 获取分页信息
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{ pageIndex: number, pageSize: number }} - 页码：页码从0开始， 每页数量
 */
export function getTablePageInfo(id) {
    let store = getStore.call(this, id),
        info = store.getPaginationInfo();
    return {
        pageIndex: Number(info.pageIndex),
        pageSize: Number(info.pageSize),
    };
}

/**
 * 未知作用 - 2207未实现
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {boolean} flag
 */
export function setCheckboxFix(id, flag) {
    let store = getStore.call(this, id);
    store.forceUpdate();
}

/**
 * 获取排序信息
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{ mode: 'single', sortParam: Array<{ field: ColKey, order: 'ascend' | 'default' | 'descend' }> }}
 */
export function getSortParam(id) {
    let store = getStore.call(this, id);
    let sortInfo = store.getSortInfo();
    return sortInfo.size ? {
        mode: 'single', // 目前貌似只有single
        sortParam: [...sortInfo].map(([colKey, order]) => {
            if (order === 1) {
                order = 'ascend';
            } else if (order === 0) {
                order = 'default';
            } else {
                order = 'descend';
            }
            return {
                field: colKey,
                order,
            };
        }),
    } : undefined;
}

/**
 * 更新数据（云原生环境下）
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {string} pkname - values中作为pk的字段名
 * @param {Array<Object<ColKey, CellValue.value>>} nextRows
 * @param {string} sagaErrorMessage
 */
export function updateDataByRefresh(id, pkname, nextRows, sagaErrorMessage) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    if (!rows?.length || !Array.isArray(nextRows)) { return; }

    let groupedRowsByKey = rows.reduce((group, row) => { //pkname的值要唯一，不然会被覆盖
        group[row.values[pkname].value] = row;
        return group;
    }, {});

    nextRows = nextRows.map(row => {
        let rawData = groupedRowsByKey[row[pkname]];

        return {
            ...rawData,
            values: {
                ...rawData.values,
                ...Object.entries(row).reduce((values, [key, value]) => {
                    values[key] = { value };
                    return values;
                }, {}),
                saga_errormesg: row.saga_status === '1' ? sagaErrorMessage : '',
            },
        };
    });
    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setRowData(nextRows));
    updateFormData.call(this, id);
}

/**
 * 2207未实现
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Object} param
 */
export function saveFilterCustomColData(id, { attrcode: colKey, rowId: rowKey, value }) {

}

/**
 * 批改方法 - 沿用2111的逻辑
 * @memberof EditTable
 * @function
 * @param config
 * @param config.areaCode
 * @param config.column
 * @param config.indexs
 * @param config.queryValue
 * @param config.pasteData
 * @param config.queryCondition
 * @param config.isManyCondition
 * @param config.extraAjaxParam
 */
export const updateAfterBatchChange = createUpdateAfterBatchChange({ getStore, updateAndAddRows });

/**
 * 增行 & 更新行
 * @private
 * @memberof EditTable
 */
export function updateAndAddRows({ rows, tableId }) {
    let store = getStore.call(this, tableId);

    if (!store || !Array.isArray(rows)) { return; } // 防止方法报错导致preventUpdate状态未解除

    let addRows = [], updateRows = [];
    rows.forEach(row => {
        let rowKey = row.key || row.rowId || row.rowid || generateKey();

        let oldRow = store.getRowData(rowKey);
        if (oldRow) {
            /** @detail rowid存在则更新行 */
            updateRows.push({
                ...oldRow,
                ...row,
                status: (!row.status || row.status === ROW_STATUS.ORIGIN) ? ROW_STATUS.EDIT : row.status,
            });
            updateFormData.call(this, tableId);
        } else {
            /** @detail rowid不存在则增行 */
            addRows.push({ ...row, key: rowKey });
        }
    });

    execWithShare.call(this, tableId, shareId => {
        let tableStore = getStore.call(this, shareId);
        if (!tableStore) { return; }

        !this.isUpdatePage && tableStore.getInstance().preventUpdate(true);

        tableStore?.setRowData(updateRows);
        tableStore?.addRow(store.getRows().length, addRows, { autoFocus: false });

        !this.isUpdatePage && tableStore.getInstance().preventUpdate(false);
        tableStore.forceUpdate();
    });
    return [];
}

/**
 * 给某列设置参照过滤
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {Object<ColKey, object | function>} data
 * @param {boolean} shouldForceUpdate - 2207未实现
 */
export function setQueryCondition(id, data, shouldForceUpdate) {
    let store = getStore.call(this, id);

    if (!store) { return; }

    Object.keys(data).forEach(key =>
        store.setColumnProps(key, { queryCondition: data[key] }),
    );

}

/**
 * 设置列属性
 * @param {ModuleId} id
 * @memberof EditTable
 * @param {Object<ColKey, ColumnProps>} data
 * @param {boolean} isShouldUpdate - 2207未实现
 */
export function setRelationedColumn(id, data, isShouldUpdate = true) {
    let store = getStore.call(this, id),
        colKeys = Object.keys(data),
        rows = store?.getRows();

    if (!store || typeof data !== 'object') { return; }

    colKeys.forEach(colKey => store.setColumnProps(colKey, data[colKey]));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, data[colKey])));

    // 抄原来的
    let meta = store.getMeta(),
        destBrowseAreaCode = meta.gridrelation?.[id]?.destBrowseAreaCode,
        destEditAreaCode = meta.gridrelation?.[id].destEditAreaCode;

    Object.keys(data).forEach(colKey => {
        /** @detail 同步到展开表单模板 */
        if (destBrowseAreaCode) {
            if (!Array.isArray(meta[destBrowseAreaCode]?.items)) { return; }

            let changeCol = meta[destBrowseAreaCode].items.find(e => e.attrcode === colKey);
            changeCol[colKey] = data[colKey];
        }

        if (!Array.isArray(destEditAreaCode) || !destEditAreaCode.length) { return; }

        /** @detail 同步到侧拉表单模板 */
        destEditAreaCode.forEach(sideId => {
            if (!Array.isArray(meta[sideId]?.items)) { return; }

            let changeCol = meta[sideId].items.find(e => e.attrcode === colKey);
            changeCol[colKey] = data[colKey];

        });
    });

    store.forceUpdate();
}

/**
 * 获取框选中的单元格的数据
 * @memberof EditTable
 * @param {ModuleId} id
 * @returns {{activeCells:Object<RowKey, ColKey[]>, activeSpecialCells: Array<{ attrcode: string, label: string }>}} activeCells：表体的，activeSpecialCells：表头的
 */
export function getActiveCells(id) {
    let store = getStore.call(this, id);
    let activeCells = store.getSelectedCell();

    /** @detail activeCells：表体选中的单元格 */
    /** @detail activeSpecialCells：表头选中的单元格 */
    /** @detail activeCell：{ rowKeyValue, attrcode }，2207给的格式不对，应该没人使用 */
    return {
        activeCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的，这里不处理
            if (typeof rowKey === 'string') {
                activeCells[rowKey] = [...colKeys];
            }
            return activeCells;
        }, {}),
        activeSpecialCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的
            if (typeof rowKey === 'number') {
                activeCells = activeCells.concat([...colKeys].map(colKey => {
                    return { attrcode: colKey, label: store.getColumnProps(colKey)?.label };
                }));
            }
            return activeCells;
        }, []),
        activeCell: store.getActiveCell(), // 应该是最后一个
    };
}

/**
 * 设置列属性
 * @memberof EditTable
 * @param {string} moduleId
 * @param {Object<ColKey, ColumnProps>} columnInfo
 */
export function setColumn(moduleId, columnInfo) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        colKeys = Object.keys(columnInfo);

    colKeys.forEach(colKey => store.setColumnProps(colKey, columnInfo[colKey]));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, columnInfo[colKey])));
}

/**
 * 获取列属性
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @returns {ColumnProps}
 */
export function getColumn(moduleId, colKey) {
    let store = getStore.call(this, moduleId);

    return store.getColumnProps(colKey);
}

/**
 * 在指定位置插入行
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {Array<{ index: DataIndex, data: TableRow }>} record
 * @returns {{ rows: TableRow[] }}
 */
export function insertDataByIndexs(moduleId, record) {
    let store = getStore.call(this, moduleId);

    record.forEach(({ index: dataIndex, data: row }) => {
        execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.addRow(dataIndex, row));
    });

    return { rows: store.getRows() };
}

/**
 * 在所有行的最后追加数据
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string} pkname
 * @param {TableRow[]} nextRows
 */
export function addTableData(moduleId, pkname, nextRows = []) {
    let store = getStore.call(this, moduleId);

    let pageInfo = store.getPaginationInfo(),
        rows = store.getRows(),
        allRowsInfo = store.getAllRowsInfo();

    /** @detail 如果传入行的rowid已经存在，则会被过滤掉 */
    nextRows = nextRows.filter(row => {
        let rowKey = row.values[pkname]?.value;
        return !store.getRowData(rowKey);
    });

    /** @detail 追加allpks */
    allRowsInfo.allpks = allRowsInfo.allpks.concat(nextRows.map(row => row.values[pkname]?.value));

    /** @detail 追加totalPage */
    pageInfo.total += nextRows.length;

    // 追加visibleRows
    if (pageInfo.pageSize > rows.length) {
        // 追加到原始数据后面，不能超过当前pageSize
        rows = rows.concat(nextRows.slice(0, pageInfo.pageSize - rows.length));
    }

    setTableData.call(this, moduleId, {
        rows,
        pageInfo,
        allpks: allRowsInfo.allpks,
    });
}

/**
 *
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string[]} pks
 * @param {number} pageIndex
 * @param {number} pageSize
 */
export function setAllRowsInfo(id, pks, pageIndex, pageSize) {
    let store = getStore.call(this, id),
        allRowsInfo = store.getAllRowsInfo(),
        { allpks } = allRowsInfo,
        { pageSize: curPageSize } = store.getPaginationInfo();

    if (typeof pks !== 'string' && !Array.isArray(pks)) { return; }

    !Array.isArray(pks) && (pks = [pks]);

    if (typeof pageSize === 'undefined') {
        pageSize = curPageSize;
    }

    if (typeof pageIndex === 'undefined') {
        allpks = [...new Set(allpks.concat(pks))];
    } else {
        allpks.splice((pageIndex + 1) * pageSize, 0, ...pks);
    }
    execWithShare.call(this, id, shareId => getStore.call(this, shareId)?.setAllRowsInfo({ ...allRowsInfo, allpks }));
}

/**
 * 存储单元格旧值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {string} value
 */
export function saveChangedRowsOldValue(id, rowKey, colKey, value) {
    let store = getStore.call(this, id);
    store.setCache(['oldRows', rowKey, colKey], value);
}

/**
 * 获取单元格旧值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @returns {string}
 */
export function getChangedRowsOldValue(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    return store.getCache(['oldRows', rowKey, colKey]);
}

/**
 * 删除单元格旧值
 * @memberof EditTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function delChangedRowsOldValue(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    store.setCache(['oldRows', rowKey, colKey], null);
}

/**
 * 更新合计行数据（只计算部分行的合计时，需要调用此方法）
 * @memberof EditTable
 * @param {ModuleId} moduleId
 */
export function updateTotal(moduleId) {
    let store = getStore.call(this, moduleId);
    store.updateTotal();
}

/**
 * 从allpks中删除某些数据
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string[]} pks
 */
export function deleteCacheId(moduleId, pks) {
    let store = getStore.call(this, moduleId);

    Array.isArray(pks) ? (pks = new Set(pks)) : (pks = new Set([pks]));

    let allRowsInfo = store.getAllRowsInfo();
    allRowsInfo.allpks = allRowsInfo.allpks.filter(e => !pks.has(e));

    execWithShare.call(this, moduleId, shareId => getStore.call(this, shareId)?.setAllRowsInfo(allRowsInfo));
}

/**
 * 增加pks
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string[] | string} pk
 */
export function addCacheId(moduleId, pk) {
    let store = getStore.call(this, moduleId),
        allRowsInfo = store.getAllRowsInfo();

    allRowsInfo.allpks = [...new Set(allRowsInfo.allpks.concat(pk))];

    execWithShare.call(this, moduleId, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore?.setAllRowsInfo(allRowsInfo);
    });
}

/**
 * 设置激活的单元格
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string} rowKey
 * @param {string} colKey
 */
export function setActiveCell(moduleId, rowKey, colKey) {
    let store = getStore.call(this, moduleId);
    store?.setActiveCell({ rowKey, colKey });
}

/**
 * 用于变换create时传入的config
 */
export function _setConfigTranslator(id, translator) {
    let store = getStore.call(this, id);
    store.setConfigTranslator(translator);
}

export function getStore(id) {
    return this.editTableDataSource[id];
}

function setStore(id) {
    this.editTableDataSource[id] = EditTable.useEditTable();
    return this.editTableDataSource[id];
}

function _addRow(id, dataIndex, values, autoFocus, callback) {
    let store = getStore.call(this, id),
        visibleRows = store.getVisibleRows(),
        key = generateKey();

    (typeof dataIndex === 'undefined' || dataIndex === -1) && (dataIndex = visibleRows.length);

    if (values instanceof Promise) {
        values.then(rowValues => {
            execWithShare.call(this, id, shareId => {
                getStore.call(this, shareId)?.addRow(dataIndex, { values: rowValues, key }, { autoFocus });
            });

            visibleRows = store.getVisibleRows();
            let addedRow = store.getVisibleRows()[visibleRows.length - 1];

            // Object.entries(rowValues).forEach(([colKey, value]) => {
            //     saveChangedRowsOldValue.call(this, id, addedRow.key, colKey, value);
            // });
            typeof callback === 'function' && callback?.(addedRow);
        });
    } else {
        values = typeof values === 'function' ? (values() ?? {}) : JSON.parse(JSON.stringify(values));

        execWithShare.call(this, id, shareId => {
            getStore.call(this, shareId)?.addRow(dataIndex, { values, key }, { autoFocus });
        });

        visibleRows = store.getVisibleRows();
        let addedRow = store.getVisibleRows()[visibleRows.length - 1];

        // Object.entries(values).forEach(([colKey, value]) => {
        //     saveChangedRowsOldValue.call(this, id, addedRow.key, colKey, value);
        // });
        typeof callback === 'function' && callback?.(addedRow);
    }
}

function _formatRow(row) {
    if (row) {
        return {
            rowid: row.rowid,
            status: row.status,
            isOptimized: row.isOptimized,
            values: row.values,
        };
    }
}

function updateFormData(moduleId) {
    let store = getStore.call(this, moduleId),
        formStore = store?.getEditFormStore(),
        rows = store?.getRows(),
        { dataIndex } = store?.getSideEditInfo() ?? {};

    rows?.[dataIndex] && formStore?.setAllFormValue(rows[dataIndex].values || {});
    this.controlRule.willRun('setTableData');
}

function execWithShare(id, callback) {
    let store = getStore.call(this, id),
        shareIds = new Set([id]);

    if (store.getGridTableId()) { // 入参id是表格id，看看是不是卡片表格的
        shareIds.add(store.getGridTableId());
    } else if (store.getConfig()?.tableId) { // 入参是卡片表格的id
        shareIds.add(store.getConfig().tableId);
    }

    [...shareIds].forEach(tabIds => {
        callback(tabIds);
    });
    this.controlRule.willRun('setTableData');
}